import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Button, Badge } from 'reactstrap';
import { Smartphone, Monitor, Tablet, Trash2, Bell } from 'react-feather';
import axios from 'axios';

const DeviceSubscriptions = ({ userUuid, onSubscriptionRemoved }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userUuid) {
      fetchUserSubscriptions();
    }
  }, [userUuid]);

  const fetchUserSubscriptions = async () => {
    try {
      const apiUrl = process.env.REACT_APP_MS_API_URL || 'http://localhost:3000';
      const response = await axios.get(`${apiUrl}/user/${userUuid}`);
      
      if (response.data && response.data.pushSubscriptions) {
        setSubscriptions(response.data.pushSubscriptions);
      }
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeSubscription = async (subscriptionId) => {
    try {
      const apiUrl = process.env.REACT_APP_MS_API_URL || 'http://localhost:3000';
      await axios.delete(`${apiUrl}/user/${userUuid}/push-subscription/${subscriptionId}`);
      
      setSubscriptions(prev => prev.filter(sub => sub.deviceInfo?.id !== subscriptionId));
      
      if (onSubscriptionRemoved) {
        onSubscriptionRemoved();
      }
      
      console.log('Subscription removed successfully');
    } catch (error) {
      console.error('Error removing subscription:', error);
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'mobile':
        return <Smartphone size={16} />;
      case 'tablet':
        return <Tablet size={16} />;
      case 'desktop':
        return <Monitor size={16} />;
      default:
        return <Bell size={16} />;
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDeviceName = (subscription) => {
    const deviceType = subscription.deviceType || 'desktop';
    const timestamp = subscription.deviceInfo?.timestamp || subscription.timestamp;
    
    return `${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)} - ${formatDate(timestamp)}`;
  };

  if (loading) {
    return (
      <Card>
        <CardBody>
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">Cargando...</span>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <h5 className="mb-0">Dispositivos Suscritos</h5>
        </CardHeader>
        <CardBody>
          <p className="text-muted">No hay dispositivos suscritos a las notificaciones.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <h5 className="mb-0">Dispositivos Suscritos ({subscriptions.length})</h5>
        <small className="text-muted">
          Las notificaciones se envían a todos los dispositivos suscritos
        </small>
      </CardHeader>
      <CardBody>
        <div className="row">
          {subscriptions.map((subscription, index) => (
            <div key={subscription.deviceInfo?.id || index} className="col-md-6 mb-3">
              <div className="d-flex align-items-center justify-content-between p-3 border rounded">
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    {getDeviceIcon(subscription.deviceType)}
                  </div>
                  <div>
                    <h6 className="mb-1">{getDeviceName(subscription)}</h6>
                    <small className="text-muted">
                      {subscription.deviceType || 'desktop'}
                    </small>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <Badge color="success" className="me-2">
                    Activo
                  </Badge>
                  <Button
                    color="outline-danger"
                    size="sm"
                    onClick={() => removeSubscription(subscription.deviceInfo?.id)}
                    title="Desuscribir dispositivo"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-3">
          <small className="text-muted">
            <strong>Nota:</strong> Cada dispositivo (teléfono, computadora, tablet) 
            necesita suscribirse por separado para recibir notificaciones.
          </small>
        </div>
      </CardBody>
    </Card>
  );
};

export default DeviceSubscriptions;
